import React, { useState, useRef, useEffect } from 'react';

function MessageView({ conversation, messages, onSend, quickReplies, ticketViews = [] }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (forceInternal = null) => {
    if (!replyText.trim() || sending) return;

    setSending(true);
    try {
      const internal = forceInternal !== null ? forceInternal : isInternalNote;
      await onSend(replyText, internal);
      setReplyText('');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';

    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }

    // Format complet avec date et heure exactes
    return date.toLocaleString('fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessageBody = (body) => {
    if (!body) return '';

    // Nettoyer le HTML : supprimer les sauts de ligne excessifs
    let cleanBody = body;

    // Remplacer les <br> multiples par un seul
    cleanBody = cleanBody.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
    // Remplacer les <p> vides
    cleanBody = cleanBody.replace(/<p>\s*<\/p>/gi, '');
    // Remplacer les &nbsp; multiples par un espace
    cleanBody = cleanBody.replace(/(&nbsp;\s*){2,}/gi, ' ');
    // Supprimer les sauts de ligne en début et fin
    cleanBody = cleanBody.replace(/^(\s*<br\s*\/?>\s*)+/gi, '');
    cleanBody = cleanBody.replace(/(\s*<br\s*\/?>\s*)+$/gi, '');
    // Nettoyer les espaces multiples
    cleanBody = cleanBody.replace(/\n\s*\n/g, '\n');

    if (cleanBody.includes('<')) {
      return <div className="message-content" dangerouslySetInnerHTML={{ __html: cleanBody }} />;
    }

    return cleanBody;
  };

  const isOutgoing = (msg) => {
    if (msg.fromMe !== undefined) {
      return msg.fromMe;
    }
    if (msg.message_type === 'comment') {
      return true;
    }
    return false;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimetype) => {
    if (!mimetype) return '📄';
    if (mimetype.startsWith('image/')) return '🖼️';
    if (mimetype.includes('pdf')) return '📕';
    if (mimetype.includes('word') || mimetype.includes('document')) return '📘';
    if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return '📗';
    if (mimetype.includes('zip') || mimetype.includes('rar')) return '📦';
    return '📄';
  };

  // Vérifier si un message est un message de tracking uniquement (sans contenu texte)
  const isTrackingOnly = (msg) => {
    const body = msg.body || '';
    const cleanBody = body.replace(/<[^>]*>/g, '').trim();
    return !cleanBody && msg.tracking && msg.tracking.length > 0;
  };

  // Rendu d'un message de tracking (changement d'étape, assignation, etc.)
  const renderTrackingMessage = (msg) => {
    return (
      <div
        key={msg.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 16px',
          margin: '10px 0'
        }}
      >
        <div style={{
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border)',
          borderRadius: '20px',
          padding: '8px 16px',
          fontSize: '0.8rem',
          color: 'var(--text-secondary)',
          maxWidth: '80%'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            {msg.author_id?.[1] || 'Système'} - {formatMessageTime(msg.date)}
          </div>
          {msg.tracking.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span style={{ color: 'var(--text-muted)' }}>{t.field}:</span>
              <span style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                padding: '2px 8px',
                borderRadius: '4px',
                textDecoration: 'line-through',
                fontSize: '0.75rem'
              }}>
                {t.oldValue || 'Aucun'}
              </span>
              <span>→</span>
              <span style={{
                background: 'rgba(34, 197, 94, 0.2)',
                color: '#22c55e',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.75rem'
              }}>
                {t.newValue || 'Aucun'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendu des médias WhatsApp
  const renderWhatsAppMedia = (msg) => {
    if (!msg.mediaUrl) return null;

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';
    const fullMediaUrl = msg.mediaUrl.startsWith('http') ? msg.mediaUrl : `${API_URL}${msg.mediaUrl}`;

    // Image
    if (msg.mediaMimetype?.startsWith('image/')) {
      return (
        <div style={{ marginBottom: '10px' }}>
          <a href={fullMediaUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={fullMediaUrl}
              alt="Image WhatsApp"
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                borderRadius: '8px',
                cursor: 'pointer',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
            <div style={{
              display: 'none',
              alignItems: 'center',
              gap: '8px',
              padding: '10px',
              background: 'var(--bg-tertiary)',
              borderRadius: '6px',
              color: 'var(--text-secondary)'
            }}>
              🖼️ Image non disponible
            </div>
          </a>
        </div>
      );
    }

    // Vidéo
    if (msg.mediaMimetype?.startsWith('video/')) {
      return (
        <div style={{ marginBottom: '10px' }}>
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '400px',
              borderRadius: '8px'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          >
            <source src={fullMediaUrl} type={msg.mediaMimetype} />
            Votre navigateur ne supporte pas la lecture vidéo.
          </video>
          <div style={{
            display: 'none',
            alignItems: 'center',
            gap: '8px',
            padding: '10px',
            background: 'var(--bg-tertiary)',
            borderRadius: '6px',
            color: 'var(--text-secondary)'
          }}>
            🎬 Vidéo non disponible - <a href={fullMediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Télécharger</a>
          </div>
        </div>
      );
    }

    // Audio (vocal)
    if (msg.mediaMimetype?.startsWith('audio/') || msg.type === 'ptt') {
      return (
        <div style={{ marginBottom: '10px' }}>
          <audio
            controls
            style={{ width: '100%', maxWidth: '300px' }}
          >
            <source src={fullMediaUrl} type={msg.mediaMimetype || 'audio/ogg'} />
            Votre navigateur ne supporte pas la lecture audio.
          </audio>
        </div>
      );
    }

    // Document / autre fichier
    return (
      <div style={{ marginBottom: '10px' }}>
        <a
          href={fullMediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px',
            background: 'var(--bg-tertiary)',
            borderRadius: '8px',
            textDecoration: 'none',
            color: 'var(--text-primary)'
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>{getFileIcon(msg.mediaMimetype)}</span>
          <div>
            <div style={{ fontWeight: '500' }}>{msg.mediaFilename || 'Document'}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Cliquez pour télécharger
            </div>
          </div>
        </a>
      </div>
    );
  };

  // Rendu d'un message normal
  const renderMessage = (msg, index) => {
    const outgoing = isOutgoing(msg);
    const hasTracking = msg.tracking && msg.tracking.length > 0;
    const hasBody = msg.body && msg.body.replace(/<[^>]*>/g, '').trim();
    const hasWhatsAppMedia = msg.mediaUrl || msg.hasMedia;

    return (
      <div
        key={msg.id || index}
        className={`message ${outgoing ? 'outgoing' : 'incoming'} ${msg.isInternal ? 'internal-note' : ''}`}
        style={{
          ...(msg.isInternal ? {
            background: 'rgba(251, 191, 36, 0.2)',
            borderLeft: '3px solid #fbbf24'
          } : {}),
          ...(msg.isDescription ? {
            background: 'var(--bg-tertiary)',
            borderLeft: '3px solid var(--accent)'
          } : {})
        }}
      >
        {/* Nom de l'agent / auteur - bien visible en haut du message */}
        {(() => {
          const authorName = msg.author_id?.[1] || msg.agentName || (msg.fromMe ? 'Agent' : null);
          if (!authorName) return null;
          const isAgent = outgoing || msg.message_type === 'comment';
          return (
            <div style={{
              fontSize: '0.75rem',
              fontWeight: '700',
              marginBottom: '4px',
              color: isAgent ? 'rgba(255,255,255,0.9)' : 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontSize: '0.7rem' }}>{isAgent ? '👤' : '🧑'}</span>
              {authorName}
            </div>
          );
        })()}

        {msg.isDescription && (
          <div style={{
            fontSize: '0.7rem',
            color: 'var(--accent)',
            marginBottom: '5px',
            fontWeight: 'bold'
          }}>
            📝 DESCRIPTION DU TICKET
          </div>
        )}

        {msg.isInternal && (
          <div style={{
            fontSize: '0.7rem',
            color: '#fbbf24',
            marginBottom: '5px',
            fontWeight: 'bold'
          }}>
            🔒 NOTE INTERNE
          </div>
        )}

        {/* Média WhatsApp */}
        {hasWhatsAppMedia && renderWhatsAppMedia(msg)}

        {/* Indicateur de média non téléchargé */}
        {msg.hasMedia && !msg.mediaUrl && (
          <div style={{
            padding: '10px',
            background: 'var(--bg-tertiary)',
            borderRadius: '8px',
            marginBottom: '10px',
            color: 'var(--text-muted)',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>📎</span>
            <span>Média ({msg.type || 'fichier'}) - Rechargez pour télécharger</span>
          </div>
        )}

        {/* Corps du message */}
        {hasBody && (
          <div className="message-body" style={{ marginBottom: hasTracking ? '10px' : '0' }}>
            {renderMessageBody(msg.body)}
          </div>
        )}

        {/* Tracking values (changements) */}
        {hasTracking && (
          <div style={{
            marginTop: hasBody ? '10px' : '0',
            padding: '8px',
            background: outgoing ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
            borderRadius: '6px'
          }}>
            {msg.tracking.map((t, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: i < msg.tracking.length - 1 ? '6px' : '0',
                fontSize: '0.8rem'
              }}>
                <span style={{ color: 'var(--text-muted)', minWidth: '80px' }}>{t.field}:</span>
                <span style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textDecoration: 'line-through',
                  fontSize: '0.75rem'
                }}>
                  {t.oldValue || 'Aucun'}
                </span>
                <span>→</span>
                <span style={{
                  background: 'rgba(34, 197, 94, 0.2)',
                  color: '#22c55e',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem'
                }}>
                  {t.newValue || 'Aucun'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Pièces jointes */}
        {msg.attachments && msg.attachments.length > 0 && (
          <div style={{
            marginTop: '10px',
            padding: '10px',
            background: outgoing ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
              📎 {msg.attachments.length} pièce(s) jointe(s)
            </div>
            {msg.attachments.map((att, i) => {
              const isImage = att.mimetype?.startsWith('image/');

              if (isImage) {
                return (
                  <div key={i} style={{ marginBottom: '10px' }}>
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block' }}
                    >
                      <img
                        src={att.url}
                        alt={att.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div style={{
                        display: 'none',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        background: 'var(--bg-primary)',
                        borderRadius: '6px',
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem'
                      }}>
                        <span>🖼️</span>
                        <span>{att.name}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                          {formatFileSize(att.size)}
                        </span>
                      </div>
                    </a>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      marginTop: '4px'
                    }}>
                      {att.name} - {formatFileSize(att.size)}
                    </div>
                  </div>
                );
              }

              return (
                <a
                  key={i}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    background: 'var(--bg-primary)',
                    borderRadius: '6px',
                    marginBottom: '5px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '0.85rem'
                  }}
                >
                  <span>{getFileIcon(att.mimetype)}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {att.name}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                    {formatFileSize(att.size)}
                  </span>
                </a>
              );
            })}
          </div>
        )}

        <div className="message-time">
          {formatMessageTime(msg.timestamp || msg.date)}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="main-header">
        <h2>
          <span className={`conversation-source ${conversation.source}`}>
            {conversation.source === 'odoo' ? 'Odoo' : 'WhatsApp'}
          </span>
          {conversation.source === 'odoo' ? conversation.title : (() => {
            // Format: PARTNER PRÉNOM (+phone) ou juste customer (+phone)
            const parts = [];
            if (conversation.partnerId && conversation.partnerName) {
              parts.push(conversation.partnerName);
              if (conversation.contactFirstname) {
                parts.push(conversation.contactFirstname.toUpperCase());
              }
            } else {
              parts.push(conversation.customer);
            }
            const displayName = parts.join(' ');
            const phone = conversation.customerPhone;
            return phone ? `${displayName} (${phone})` : displayName;
          })()}
        </h2>
        <div className="header-actions">
          {conversation.team && (
            <span style={{
              padding: '6px 12px',
              background: 'var(--bg-tertiary)',
              borderRadius: '15px',
              fontSize: '0.8rem',
              marginRight: '10px'
            }}>
              {conversation.team}
            </span>
          )}
        </div>
      </div>

      {/* Sous-header avec info client pour Odoo et WhatsApp (si lié) */}
      {(conversation.source === 'odoo' || conversation.partnerId) && (
        <div style={{
          padding: '10px 20px',
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          fontSize: '0.85rem'
        }}>
          <span style={{ color: 'var(--text-muted)' }}>Client:</span>
          <span style={{ fontWeight: '600' }}>
            {conversation.partnerId && conversation.partnerName
              ? `${conversation.partnerName}${conversation.contactFirstname ? ' ' + conversation.contactFirstname.toUpperCase() : ''}`
              : conversation.customer}
          </span>
          {conversation.partnerEmail && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>|</span>
              <a href={`mailto:${conversation.partnerEmail}`} style={{ color: 'var(--accent)' }}>
                {conversation.partnerEmail}
              </a>
            </>
          )}
          {conversation.partnerPhone && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>|</span>
              <a href={`tel:${conversation.partnerPhone}`} style={{ color: 'var(--accent)' }}>
                {conversation.partnerPhone}
              </a>
            </>
          )}
          {conversation.source === 'whatsapp' && conversation.partnerId && (
            <span style={{
              padding: '2px 8px',
              background: 'rgba(124, 58, 237, 0.1)',
              color: '#7c3aed',
              borderRadius: '10px',
              fontSize: '0.7rem',
              fontWeight: '600'
            }}>
              Odoo
            </span>
          )}
        </div>
      )}

      <div className="messages-container">
        {(() => {
          // Fusionner messages et vues dans un seul flux chronologique
          const items = [];

          // Ajouter les messages avec leur timestamp normalisé
          messages.forEach((msg, index) => {
            let ts;
            if (msg.timestamp) {
              ts = typeof msg.timestamp === 'number' ? msg.timestamp * 1000 : new Date(msg.timestamp).getTime();
            } else if (msg.date) {
              ts = new Date(msg.date).getTime();
            } else {
              ts = 0;
            }
            items.push({ type: 'message', data: msg, index, ts });
          });

          // Ajouter les vues agents avec leur timestamp
          ticketViews.forEach((view, i) => {
            const ts = new Date(view.viewed_at).getTime();
            items.push({ type: 'view', data: view, index: i, ts });
          });

          // Trier par timestamp chronologique
          items.sort((a, b) => a.ts - b.ts);

          // Regrouper les vues consécutives qui sont proches (< 2 min d'écart)
          const rendered = [];
          let i = 0;
          while (i < items.length) {
            const item = items[i];

            if (item.type === 'message') {
              if (isTrackingOnly(item.data)) {
                rendered.push(renderTrackingMessage(item.data));
              } else {
                rendered.push(renderMessage(item.data, item.index));
              }
              i++;
            } else {
              // Collecter les vues consécutives
              const viewGroup = [item.data];
              let j = i + 1;
              while (j < items.length && items[j].type === 'view') {
                viewGroup.push(items[j].data);
                j++;
              }
              // Afficher le groupe de vues
              rendered.push(
                <div key={`views-${i}`} style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  justifyContent: 'center',
                  padding: '4px 16px',
                  margin: '4px 0'
                }}>
                  {viewGroup.map((view, vi) => (
                    <div key={vi} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '3px 10px',
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border)',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <span style={{ fontSize: '0.65rem' }}>👁️</span>
                      <span style={{ fontWeight: '600' }}>{view.agent_name}</span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {new Date(view.viewed_at).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              );
              i = j;
            }
          }

          return rendered;
        })()}

        <div ref={messagesEndRef} />
      </div>

      <div className="reply-container">
        {/* Toggle note interne / message externe */}
        {conversation.source === 'odoo' && (
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '15px',
            padding: '10px',
            background: 'var(--bg-primary)',
            borderRadius: '8px'
          }}>
            <button
              className={`btn ${!isInternalNote ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setIsInternalNote(false)}
              style={{ flex: 1 }}
            >
              💬 Message au client
            </button>
            <button
              className={`btn ${isInternalNote ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setIsInternalNote(true)}
              style={{ flex: 1, background: isInternalNote ? '#fbbf24' : undefined, color: isInternalNote ? '#000' : undefined }}
            >
              🔒 Note interne
            </button>
          </div>
        )}

        <div className="quick-replies">
          {quickReplies.map(qr => (
            <button
              key={qr.id}
              className="quick-reply"
              onClick={() => setReplyText(qr.content)}
            >
              {qr.title}
            </button>
          ))}
        </div>

        <div className="reply-input-container">
          <textarea
            className="reply-input"
            placeholder={isInternalNote ? "Écrire une note interne (visible uniquement par l'équipe)..." : "Tapez votre réponse au client..."}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            style={isInternalNote ? { borderColor: '#fbbf24' } : {}}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <button
              className="send-btn"
              onClick={() => handleSend()}
              disabled={!replyText.trim() || sending}
              style={isInternalNote ? { background: '#fbbf24', color: '#000' } : {}}
            >
              {sending ? '...' : isInternalNote ? '🔒 Note' : '📤 Envoyer'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default MessageView;
